import { useActiveAssignment } from "@/store/selectors/assignmentSelectors";
import { Button, Divider, Group, Loader, Space, Stack } from "@mantine/core";
import { Text } from "../ui/Text";
import { useAssignmentPromptConfigs } from "./assignmentPromptConfigs";
import {
  IconCheck,
  IconExclamationCircle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { IAssignmentPrompt } from "@/types/AssignmentTypes";
import { useEffect, useState } from "react";
import { AssignmentService } from "@/services/AssignmentService";
import { Textarea } from "../ui/Textarea";
import { ActionIcon } from "../ui/ActionIcon";
import { useStyles } from "@/styles/useStyles";
import { AssignmentStepNumber } from "./AssignmentStepNumber";

export function AssignmentPromptConfiguration({
  stepNumber,
}: {
  stepNumber: number;
}) {
  const activeAssignment = useActiveAssignment();
  const prompts = Object.values(activeAssignment?.prompts || {});
  const assignmentPromptConfigs = useAssignmentPromptConfigs();

  async function handleAddPrompt(promptConfig: Partial<IAssignmentPrompt>) {
    if (!activeAssignment?.id) return;

    AssignmentService.addPromptToAssignment({
      assignmentId: activeAssignment?.id,
      promptData: promptConfig,
    });
  }

  async function handleRemovePrompt(promptConfig: Partial<IAssignmentPrompt>) {
    if (!activeAssignment?.id) return;
    if (!promptConfig.id) return;

    AssignmentService.removePromptFromAssignment({
      assignmentId: activeAssignment?.id,
      promptId: promptConfig.id,
      promptData: promptConfig as IAssignmentPrompt,
    });
  }

  const customPrompts = prompts.filter(
    (prompt) => prompt.key === "custom",
  ) as IAssignmentPrompt[];

  return (
    <Stack gap="xs">
      <Group justify="space-between" wrap="nowrap">
        <Text fw={700} size="sm">
          <AssignmentStepNumber stepNumber={stepNumber} /> Add reflection
          questions
        </Text>
        {/* <SupportVideoButton supportVideoId="assignmentReflectionQuestions" /> */}
      </Group>
      {assignmentPromptConfigs.map((prompConfigData) => {
        return (
          <SingleAssignmentPrompt
            key={prompConfigData.key}
            promptConfig={prompConfigData}
            onAddPrompt={handleAddPrompt}
            onRemovePrompt={handleRemovePrompt}
          />
        );
      })}
      <Divider />
      <Button
        variant="light"
        size="sm"
        radius="xl"
        color="slate"
        onClick={() => {
          if (!activeAssignment?.id) return;
          AssignmentService.addCustomPromptToAssignment({
            assignmentId: activeAssignment?.id,
          });
        }}
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <IconPlus size={16} />
        <Space w="xs" />
        <Text fw={700} size="sm">
          Add custom reflection question
        </Text>
      </Button>
      {customPrompts.map((prompt) => {
        return (
          <SingleAssignmentPrompt
            key={prompt.id}
            promptConfig={prompt}
            onAddPrompt={handleAddPrompt}
            onRemovePrompt={handleRemovePrompt}
          />
        );
      })}
    </Stack>
  );
}

function SingleAssignmentPrompt({
  promptConfig,
  onAddPrompt,
  onRemovePrompt,
}: {
  promptConfig: Partial<IAssignmentPrompt>;
  onAddPrompt: (promptConfig: Partial<IAssignmentPrompt>) => void;
  onRemovePrompt: (promptConfig: Partial<IAssignmentPrompt>) => void;
}) {
  const { theme } = useStyles();
  const [isHovered, setIsHovered] = useState(false);
  const [isChangingValue, setIsChangingValue] = useState(false);
  const activeAssignment = useActiveAssignment();
  const promptAlreadyAdded = Object.values(
    activeAssignment?.prompts || {},
  ).find((prompt) => prompt.key === promptConfig.key);
  const isCustomPrompt = promptConfig.key === "custom";

  useEffect(() => {
    if (promptConfig?.prompt) {
      setIsChangingValue(true);
      setTimeout(() => {
        setIsChangingValue(false);
      }, 1000);
    }
  }, [promptConfig?.prompt]);

  const isPromptAlreadyAdded = !!promptAlreadyAdded;

  if (isCustomPrompt) {
    return (
      <Stack w="100%" gap="xs">
        <Group gap="xs">
          <Textarea
            id={promptConfig.id || "custom-prompt"}
            autosize
            placeholder="Enter your custom prompt here"
            flex={1}
            size="sm"
            radius="xl"
            value={promptConfig.prompt || ""}
            styles={{
              input: {
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 16,
                paddingRight: 16,
                fontWeight: 500,
                fontSize: 14,
              },
            }}
            onDebouncedChange={(value) => {
              AssignmentService.updateCustomPrompt({
                assignmentId: activeAssignment?.id || "",
                promptId: promptConfig.id || "",
                prompt: value,
              });
            }}
            rightSection={
              !promptConfig.prompt ? (
                <IconExclamationCircle size={16} color={theme.colors.red[7]} />
              ) : isChangingValue ? (
                <Loader size="xs" color={theme.colors.primary[7]} />
              ) : (
                <IconCheck size={16} color={theme.colors.primary[7]} />
              )
            }
          />
          <ActionIcon
            variant="light"
            color="red"
            radius="xl"
            onClick={() => onRemovePrompt(promptConfig)}
            ariaLabel="Remove prompt"
            size="lg"
          >
            <IconTrash size={20} />
          </ActionIcon>
        </Group>
      </Stack>
    );
  }
  return (
    <Stack w="100%" gap="xs">
      <Button
        color={
          isHovered && promptAlreadyAdded
            ? "red"
            : promptAlreadyAdded
              ? "primary.9"
              : "text.9"
        }
        variant={promptAlreadyAdded ? "light" : "outline"}
        size="sm"
        radius="xl"
        py={6}
        h={isHovered ? "auto" : "auto"}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() =>
          isPromptAlreadyAdded
            ? onRemovePrompt(promptAlreadyAdded)
            : onAddPrompt(promptConfig)
        }
        styles={{
          inner: {
            // textAlign: "left",
            justifyContent: "space-between",
            // alignItems: "flex-start",
            width: "100%",
            flex: 1,
          },
        }}
        rightSection={
          promptAlreadyAdded ? (
            isHovered ? (
              <IconTrash size={16} />
            ) : (
              <IconCheck size={16} />
            )
          ) : (
            <IconPlus size={16} />
          )
        }
        // w="max-content"
      >
        <Text
          fw={500}
          size="sm"
          w="100%"
          flex={1}
          maw="100%"
          lineClamp={isHovered ? 5 : isPromptAlreadyAdded ? 1 : 2}
          px={4}
          ta="left"
        >
          {promptConfig.prompt}
        </Text>
        <Space w="xs" />
      </Button>
    </Stack>
  );
}
